import React, { useState } from 'react'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import classNames from 'classnames'
import { flowRight, isEqual } from 'lodash'
import { graphql } from '@apollo/client/react/hoc'
import { withTranslationCustom } from 'lib/i18next'
import { getVehiclePicturesEditModalPath, getVehicleDisplayName, sortVehicleImages } from 'lib/utils/store/vehicle'
import { logException } from 'lib/errors'
import { withRouter } from 'react-router-dom'

import ImageUpload from 'components/Upload/ImageUpload'
import Button from 'components/UI/Button'
import LoadingIndicator from 'components/LoadingIndicator'
import ErrorComponent from 'components/Errors/ErrorComponent'
import VehicleProfileLogic from 'components/Vehicles/VehicleProfile/VehicleProfileLogic'
import ImageContainer from 'components/ImageContainer'
import VehicleFrame from 'images/svg/vehicle-frame.svg'
import Link from 'components/Link'
import AppContext from 'components/AppContext'
import FileUploadLogic from 'components/Upload/FileUploadLogic'
import InfoInline from 'components/UI/InfoInline/InfoInline'
import ImageEdit from 'components/Image/ImageEdit/ImageEdit'
import IconButton from 'components/UI/IconButton/IconButton'
import YesNo from 'components/UI/YesNo/YesNo'
import { OverlayGalleryControllerContext } from 'components/Gallery/OverlayGallery/OverlayGallery'

import deleteVehicleImages from 'graphql/vehicle/deleteVehicleImages.gql'

import './vehicle-pictures.pcss'

const IMAGES_TO_SHOW = 6

const RenderImage = SortableElement((imageProps) => {
    const {
        image,
        onSelect,
        selectedImages,
        editMode,
        deleting,
        profileImage,
        t,
        onImageSelect,
        index,
        onSelectForEdit,
        threeImagesInRow
    } = imageProps

    const [deleteConfirmationActive, setDeleteConfirmationActive] = useState(false)

    const isSelected = () => {
        return selectedImages.find(img => img.id === image.id)
    }

    const isProfileImage = profileImage === image.profile

    if (isSelected() && deleting) {
        return <div className="vehicle-picture-wrapper vehicle-picture--loading"><LoadingIndicator /></div>
    }

    return (
        <div
            className={classNames(
                'vehicle-picture-wrapper',
                { selected: isSelected() },
                { 'profile-image': isProfileImage },
                { 'three-in-row': threeImagesInRow }
            )}
        >
            {editMode && (
                <>
                    <div className="vehicle-picture__actions">
                        <div
                            className={classNames(
                                { 'vehicle-picture__action vehicle-picture__action--delete': !deleteConfirmationActive },
                                { 'vehicle-picture__action vehicle-picture__action--deleting': deleteConfirmationActive }
                            )}
                        >
                            <YesNo
                                renderStartButton={(props) => (
                                    <IconButton
                                        icon={['fal', 'times']}
                                        iconColor="white"
                                        trackName="deleteImage"
                                        alt={t('common:delete')}
                                        {...props}
                                    />
                                )}
                                startTrackName="delete"
                                yesText={t('common:delete')}
                                onActiveChange={setDeleteConfirmationActive}
                                onYes={() => onSelect(image)}
                                yesTrackName="confirmDelete"
                                noText={t('common:cancel')}
                                noTrackName="cancelDelete"
                            />
                        </div>
                    </div>
                    <div className="vehicle-picture__actions vehicle-picture__actions--bottom-left">
                        {!deleteConfirmationActive && (
                            <Button
                                className="vehicle-picture__action vehicle-picture__action--edit"
                                onClick={() => onSelectForEdit(image)}
                                trackName="imageEdit"
                            >
                                {t('common:edit')}
                            </Button>
                        )}
                    </div>
                </>
            )}
            {(isProfileImage && editMode && !deleteConfirmationActive) && (
                <span className="profile-image-text">{t('vehicleProfile:pictures.profileImageLabel')}</span>
            )}
            <VehicleFrame className="vehicle-picture__vehicle-frame" />
            <Link
                to={image.thumb}
                onClick={(e) => {
                    e.preventDefault()
                    onImageSelect(index)
                }}
                trackName="imageOpen"
            >
                <ImageContainer
                    src={image.profile}
                    alt="vehicle"
                    absolute
                />
            </Link>
        </div>
    )
})

const RenderImages = SortableContainer((props) => {
    const {
        images,
        onSelect,
        selectedImages,
        editMode,
        deleting,
        showRest,
        allVisible,
        ...rest
    } = props

    return (
        <div className="vehicle-pictures__list">
            {images.map((img, index) => {
                if (index + 1 > IMAGES_TO_SHOW && !allVisible && !editMode) return null

                return (
                    <RenderImage
                        key={img.id}
                        index={index}
                        image={img}
                        onSelect={onSelect}
                        selectedImages={selectedImages}
                        editMode={editMode}
                        deleting={deleting}
                        disabled={!editMode}
                        {...rest}
                    />
                )
            })}
            {(!allVisible && !editMode) && (
                <div className="vehicle-picture-wrapper vehicle-picture--load-more">
                    <Button
                        className="button button--tertiary"
                        onClick={() => showRest()}
                        trackName="showRest"
                    >
                        Näytä kaikki
                    </Button>
                </div>
            )}
        </div>
    )
})

class VehiclePictures extends React.Component {
    constructor (props) {
        super(props)

        const initialImages = props.images

        this.state = {
            uploadSuccess: null,
            imagesToDelete: [],
            images: sortVehicleImages([...initialImages], props.vehicle.profileImage.profile),
            deleting: false,
            allVisible: initialImages.length <= IMAGES_TO_SHOW,
            selectedIndex: 0,
            editMode: initialImages.length <= 0 || props.editMode,
            imageToEdit: null,
            uploadImageSelected: null,
        }
    }

    componentDidUpdate (prevProps) {
        if (prevProps.images.length !== this.props.images.length || !isEqual(prevProps.images, this.props.images)) {
            this.setState({
                images: sortVehicleImages([...this.props.images], this.props.vehicle.profileImage.profile)
            })
        }

        if (prevProps.editMode !== this.props.editMode) {
            this.setState({ editMode: this.props.editMode })
        }
    }

    async updateProfileImage (imageId, updateVehicle) {
        const { id } = this.props.vehicle

        await updateVehicle(id, {
            profileImageId: imageId
        })
    }

    handleDragEnd = (e, updateVehicle) => {
        const { images } = this.state
        const { profileImage } = this.props.vehicle

        const moveElement = (array, fromIndex, toIndex) => {
            const element = array[fromIndex]
            array.splice(fromIndex, 1)
            array.splice(toIndex, 0, element)
            return array
        }

        const sortedImages = moveElement([...images], e.oldIndex, e.newIndex)

        this.setState({
            images: sortedImages
        })

        if (sortedImages[0].profile !== profileImage.profile) {
            this.updateProfileImage(sortedImages[0].id, updateVehicle)
        }
    }

    handleSelectImageForDelete = async (image) => {
        if (this.state.imagesToDelete.find(img => img.id === image.id)) {
            this.setState({
                imagesToDelete: this.state.imagesToDelete.filter(img => img.id !== image.id)
            })
        } else {
            this.setState({
                imagesToDelete: [...this.state.imagesToDelete, image]
            })
        }

        this.setState({ deleting: true })

        await this.props.deleteVehicleImages({
            variables: { images: image.id },
            refetchQueries: ['getVehicleProfileView'],
            awaitRefetchQueries: true,
        }).catch(error => {
            logException(error)
        })

        this.setState({ deleting: false })
    }

    handleCancel () {
        this.props.onClose()
    }

    handleShowRest = () => {
        this.setState({ allVisible: true })
    }

    renderActions () {
        const {
            t,
            location,
            vehicle,
            hideEditAction,
            renderButtonsPortalTarget,
        } = this.props

        const { editMode } = this.state

        if (hideEditAction && !editMode) {
            return null
        }

        const displayEditButton = vehicle.images && vehicle.images.length > 0 && !editMode

        const editButton = () => {
            return (
                <Link
                    to={getVehiclePicturesEditModalPath(location, vehicle.id)}
                    className="button"
                    trackName="editImages"
                >
                    {t('vehicleProfile:pictures.editOrAddPictures')}
                </Link>
            )
        }

        const closeButton = (
            <Button
                className={displayEditButton ? 'button button--secondary' : 'button'}
                type="button"
                onClick={() => this.handleCancel()}
                trackName="done"
            >
                {(!displayEditButton && vehicle.images && vehicle.images.length > 0) ? t('common:done') : t('common:close')}
            </Button>
        )

        return (
            <>
                <div className="action-wrapper s-up-only">
                    {displayEditButton && editButton()}
                    {closeButton}
                </div>
                {renderButtonsPortalTarget && (
                    ReactDOM.createPortal(
                        (displayEditButton && editButton()) || closeButton,
                        renderButtonsPortalTarget
                    )
                )}
            </>
        )
    }

    handleUploadImage = (uploadVehicleImage) => {
        return async (file, cropData) => {
            const { vehicle } = this.props
            const res = await uploadVehicleImage(vehicle.id, file, cropData)
            if (res.data.uploadVehicleImage && res.data.uploadVehicleImage.images) {
                this.setState({
                    images: sortVehicleImages([...res.data.uploadVehicleImage.images], this.props.vehicle.profileImage.profile)
                })
            }
            this.setState({ uploadImageSelected: null })
        }
    }

    handleImageCrop = async (imageId, cropData, updateVehicleImageCrop) => {
        this.setState({ imageToEdit: null })
        const { id } = this.props.vehicle

        await updateVehicleImageCrop(id, cropData, imageId)
    }

    render () {
        const { imagesToDelete, images, deleting, allVisible, editMode, imageToEdit, uploadImageSelected } = this.state
        const { vehicle, t, setModalTitle } = this.props

        return (
            <AppContext context="pictures">
                <VehicleProfileLogic>
                    {({ updateVehicle, updateVehicleImageCrop, error }) => (
                        <>
                            {error && <ErrorComponent error={error} />}
                            {imageToEdit ? (
                                <ImageEdit
                                    image={imageToEdit}
                                    onCancel={() => this.setState({ imageToEdit: null })}
                                    onSave={(id, cropData) => this.handleImageCrop(id, cropData, updateVehicleImageCrop)}
                                    previewName={getVehicleDisplayName(vehicle, t)}
                                    mode="crop"
                                />
                            ) : (
                                <div className="vehicle-pictures">
                                    <FileUploadLogic>
                                        {(fileUploadLogic) => (
                                            <>
                                                {editMode && (
                                                    <>
                                                        <ImageUpload
                                                            allowMultiple={true}
                                                            onUploadImage={this.handleUploadImage(fileUploadLogic.uploadVehicleImage)}
                                                            maxSize={15000000}
                                                            uploading={fileUploadLogic.loading}
                                                            onImageSelected={image => {
                                                                this.setState({ uploadImageSelected: image })

                                                                if (image) {
                                                                    setModalTitle && setModalTitle(t('vehicleProfile:pictures.newPicture'))
                                                                } else {
                                                                    setModalTitle && setModalTitle(t('vehicleProfile:pictures.modal.editTitle'))
                                                                }
                                                            }}
                                                        />
                                                        {(images.length > 1 && !uploadImageSelected) && (
                                                            <InfoInline hideIcon={true} text={t('vehicleProfile:pictures.orderPicturesTip')} />
                                                        )}
                                                    </>
                                                )}
                                                {!uploadImageSelected && (
                                                    <OverlayGalleryControllerContext.Consumer>
                                                        {({ openGallery }) => (
                                                            <RenderImages
                                                                helperClass="vehicle-picture-wrapper--dragging"
                                                                axis="xy"
                                                                images={images}
                                                                onSortEnd={e => this.handleDragEnd(e, updateVehicle)}
                                                                onSelect={this.handleSelectImageForDelete}
                                                                selectedImages={imagesToDelete}
                                                                editMode={editMode}
                                                                deleting={deleting}
                                                                showRest={this.handleShowRest}
                                                                allVisible={allVisible}
                                                                profileImage={vehicle.profileImage.profile}
                                                                onImageSelect={(index) => openGallery(images.map(img => ({ src: img.profile })), index)}
                                                                onSelectForEdit={image => this.setState({ imageToEdit: image })}
                                                                threeImagesInRow={this.props.threeImagesInRow}
                                                                t={t}
                                                            />
                                                        )}
                                                    </OverlayGalleryControllerContext.Consumer>
                                                )}
                                            </>
                                        )}
                                    </FileUploadLogic>
                                    {!uploadImageSelected && (
                                        <>
                                            {this.renderActions()}
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </VehicleProfileLogic>
            </AppContext>
        )
    }
}

VehiclePictures.propTypes = {
    images: PropTypes.array.isRequired,
    vehicle: PropTypes.object.isRequired,
    hideEditAction: PropTypes.bool,
    threeImagesInRow: PropTypes.bool,
    setModalTitle: PropTypes.func,
}

export default flowRight(
    graphql(deleteVehicleImages, {
        name: 'deleteVehicleImages'
    }),
    withTranslationCustom(['common', 'formElements', 'vehicleProfile']),
    withRouter,
)(VehiclePictures)
